import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ProductMessage, ProductQnaResponseData } from '../../models/product-qa-chat';
import { FormsModule } from '@angular/forms';
import { LoaderComponent } from '../loader/loader.component';
import { CommonModule } from '@angular/common';
import { ChatService } from '../../services/chat.service';
import { FeedbackDetailComponent } from '../feedback-detail/feedback-detail.component';
import { Messages, Product } from '../../models/response';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { v4 as uuidv4 } from 'uuid';
import { ProductChatDescriptionComponent } from '../product-chat-description/product-chat-description.component';
import { SnackBarService } from '../../services/snack-bar.service';
import { FeedbackMainComponent } from '../feedback/feedback.component';
import { AnalyticsService } from '../../services/analytics.service';

@Component({
  selector: 'app-product-qa',
  standalone: true,
  imports: [
    MatIconModule,
    FormsModule,
    LoaderComponent,
    CommonModule,
    FeedbackDetailComponent,
    ProductChatDescriptionComponent,
    FeedbackMainComponent
  ],
  templateUrl: './product-qa.component.html',
  styleUrl: './product-qa.component.scss'
})
export class ProductQaComponent implements OnChanges {
  
  // product messages
  @Input() productMessages:ProductMessage[] = [];
  // suggestions to be shown for a product
  suggestions:string[]  = [];
  // selected product
  @Input() selectedProduct:Product = {};
  // show product panel variable to display product q and a
  @Input() showProductPanel = false;
  // mobile view boolean value
  @Input() mobileView = false;
  // parent conversation id
  @Input() parentUniqueConversationId = '';
  // child conversation id
  childUniqueConversationId = '';
  // product conversation loading
  productConversationLoading = false;
  // product qa user input
  userInputProductQA = '';
  // feedback detail flag
  feedbackDetail = false;
  // feedback reason
  feedbackReason = '';
  // selected reason
  feedbackReasonText = '';
  // Event to close the product q and a panel
  @Output() closePanelEvent = new EventEmitter<boolean>()
  // agent type
  @Input() type = '';
  // selected products
  @Input() selectedProducts:Product[] = [];
  // combined product ids for comparison
  combinedProductIds = '';
  // store
  @Input() store: string | undefined = '';
  // state
  @Input() state: string | undefined = '';
  // source
  @Input() source = '';

  constructor(private sanitizer: DomSanitizer,private snackBarService:SnackBarService, private chatService:ChatService, private analyticsService: AnalyticsService){}

  ngOnChanges(): void {
    // if messages length is zero and type is qna then intitiate a qna conversation
    if (Object.keys(this.selectedProduct).length > 0 && this.productMessages.length === 0 && this.type === 'qna') {
      this.childUniqueConversationId = uuidv4();
      // Initialize the conversation
      const productId = JSON.stringify([this.selectedProduct.id]);
      this.getAgentResponse(productId,true);
      this.analyticsService.trackPageView('/product-qa', 'Product Q&A');
    }

    // if selectedProducts length is zero and type is comparison then intitiate a comparison conversation
    if (this.selectedProducts.length > 0 && this.productMessages.length === 0 && this.type === 'comparison') {
      this.childUniqueConversationId = uuidv4();
      // Initialize the conversation
      const productIds:string[] = [];
      this.combinedProductIds = '';
      this.selectedProducts.forEach((product:Product) => {
        if(product.id){
          productIds.push(product.id);
        }
      });
      this.combinedProductIds = productIds.join(',');
      this.getAgentResponse(this.combinedProductIds,true);
      this.analyticsService.trackPageView('/product-compare', 'Product Compare');
    }
  }

  initializeNewConversation(){
    this.childUniqueConversationId = uuidv4();
    if(this.type === 'qna'){
      const productId = JSON.stringify([this.selectedProduct.id]);
      this.getAgentResponse(productId, true);
    } else {
      this.getAgentResponse(this.combinedProductIds,true);
    }
  }

  sendMessage() {
    // Make suggestions empty when the user types in text box
    this.suggestions = [];
    this.sendUserResponseProductQa();
  }

  sendUserResponseProductQa(){
    if (this.userInputProductQA.trim()) {
      this.productConversationLoading = true;
      // removing the special characters from text
      this.userInputProductQA = this.userInputProductQA.replace(/[^a-zA-Z0-9\s/,]/g, '');
      // trim the text for any spaces
      this.userInputProductQA = this.userInputProductQA.trim();
      if(this.userInputProductQA.length === 0){
        this.snackBarService.openSnackBar("Please do not enter special characters or empty string", "Dismiss", 5000);
        return;
      }
      this.productMessages.push({ sender: 'user', text: this.userInputProductQA , responseId: '0', feedback: 'None', messageType: 'user_message', products: [], answerSelected: false, multiselectOptions: [], selectedMultiselectOptions: []});
      this.getAgentResponse(this.userInputProductQA, false);
      this.userInputProductQA = '';
    }
  }

  getAgentResponse(userInput:string | undefined, flag:boolean) {
    this.productConversationLoading = true;
    const userObj = this.chatService.userInfo;
    this.chatService.productQnAandComparison(userInput,flag,this.parentUniqueConversationId, this.childUniqueConversationId, userObj, this.type, this.store, this.state, this.source).subscribe({
      next: (res:ProductQnaResponseData) => {
        const messages = res.messages;
        const responseId = res.responseId;
        let new_session = false;
        messages.forEach((message:Messages) => {
          if(message){
            if(message.new_session){
              new_session = true;
            };
            this.productMessages.push({ sender: 'bot', text: message.display_text, responseId: responseId, feedback: 'None', messageType: message.message_type, additionalFeedbackFlag: false, products: [], answerSelected: false, multiselectOptions: [], selectedMultiselectOptions: []});
          };
        });
        // get related questions
        this.suggestions = res.related_questions;
        this.productConversationLoading = false;
        if(new_session){
          // to initialize a new conversation if new_session is there in message keys
          this.initializeNewConversation();
        }
      }, error: (error) => {
        this.productConversationLoading = false;
        this.snackBarService.showErrorMessage(error.error.statusCode);
      }
    });
  }

  sendSuggestion(suggestion:string){
    this.userInputProductQA = suggestion;
    this.suggestions = [];
    this.sendMessage();
  }

  closePanel(){
    this.closePanelEvent.emit(false);
  }

  getSafeHTML(html:string):SafeHtml{
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

}
